import seek, { seekTypes } from '@adc/seek'
import fetch from '@adc/fetch'
import { gql } from '@adc/sdk'
import { TermsCard, utils } from '@adc/ui-adc'
import { GRAPH_LISTING_QUERY } from '../queries/getListing'

const { symbols } = utils

const TERMS_CARDS_REFERENCE = TermsCard[symbols.GRAPH_API_FRAGMENT_REFERENCE]
const TERMS_CARDS_FRAGMENT = TermsCard[symbols.GRAPH_API_FRAGMENT_DEFINITION]

export const getListing = async (req, listingId, isRemoteBiddingEnabled) => {
  const seekQuery = seek.for(seekTypes.LISTINGS).size(1)
  seekQuery._query = {
    toObject: () => ({
      and: {
        expressions: [
          {
            equals: {
              fields: 'listing_id',
              values: [+listingId],
            },
          },
        ],
      },
    }),
  }
  const dsl = JSON.stringify(seekQuery.toObject(false))
  const res = await fetch.forAuctionGraph(
    {
      query: GRAPH_LISTING_QUERY,
      variables: {
        dsl,
        hasAuthenticatedUser: Boolean(req?.user.logged_in),
        isRemoteBiddingEnabled: Boolean(isRemoteBiddingEnabled),
      },
    },
    { req }
  )

  return res.body.data.seek.content[0]
}

export const getListingForSealedBidOfferManagement = (req, listingId) =>
  fetch
    .forAuctionGraph(
      {
        // eslint-disable-next-line @adc/named-gql-operations
        query: gql`
          ${TERMS_CARDS_FRAGMENT}
          query resiPdpListingForSealedBid($listingId: ID!) {
            listing(listingId: $listingId) {
              # These are specific to the PDP as a buyer (sellers wouldn't ever fetch this)
              is_current_user_registered_for_online_auction

              valuation {
                seller_current_value_amount
              }
              
              ${TERMS_CARDS_REFERENCE}

              seller_terms {
                inspection_terms {
                  is_option_contingency
                  is_contingency
                }
                leaseback_terms {
                  leaseback_period_in_days
                  leaseback_period_rent
                }
                finance_terms {
                  finance_preference
                  is_contingency
                }
              }
              
              current_user_online_registration {
                registration_status
              }

              # This is information that should come from ListingLayout as an attached component fragment. NOTE: The query as it
              # exists today overfetches a lot for sellers (all parties and not the specific party we would need to render agent information etc)
              listing_id
              urn
              auction {
                start_date
              }
              listing_configuration {
                asset_type
              }
              listing_status
              listing_page_path
              formatted_address(format: DOUBLE_LINE)
              primary_photo

              program_configuration {
                buyers_premium_commission_type
                buyers_premium_primary_amount
                buyers_premium_minimum_amount
                earnest_money_deposit_commission_type
                earnest_money_deposit_minimum_amount
                earnest_money_deposit_primary_amount
                program_enrollment_code
              }

              primary_property {
                property_id
                summary {
                  total_bedrooms
                  total_bathrooms
                  square_footage
                  address {
                    coordinates {
                      lat
                      lon
                    }
                  }
                }
                is_newly_listed
                current_user_tracking_state {
                  is_seen
                  is_updated
                }
              }

              parties {
                party_role
                party_name
                party_phone
                party_email
              }

              selling_method {
                __typename

                ... on OnlineAuctionSegment {
                  # Specific to this user. Some of these should come from a blueprint fragment
                  current_user_bidding_status

                  configuration {
                    review_date
                    is_registration_enabled
                  }

                  # This is for idempotency checks from blueprint collection
                  update_date
                  online_segment_id
                  listing_id

                  # This is a guard to make sure we don't allow english auctions
                  segment_type

                  # This shows highest and best rounds
                  segment_status

                  #TODO: rely on bid ranking bid below rather than current_user_highest_bid for sealed bid path
                  current_user_highest_bid {
                    listing_id
                    bid_amount
                    bid_id
                    bidder_party_id
                    type
                    version
                    updated_date
                    created_date
                    ...OnlineAuctionSegmentBidTermsFragment
                    negotiation_sequence {
                      counters {
                        listing_id
                        bid_amount
                        bid_id
                        bidder_party_id
                        type
                        version
                        updated_date
                        created_date
                        ...OnlineAuctionSegmentBidTermsFragment
                      }
                      latest_counter_bid {
                        listing_id
                        bid_amount
                        bid_id
                        bidder_party_id
                        type
                        version
                        updated_date
                        ...OnlineAuctionSegmentBidTermsFragment
                      }
                    }
                  }

                  current_user_max_bid {
                    max_bid_amount
                    record_status
                  }

                  # TODO: This likely has some buyer authorization scoping to only see belongs_to_current_user
                  bids_ranking {
                    listing_id
                    online_segment_id

                    ranking {
                      bid_id
                      party_id
                      rank
                      seller_assigned_score
                      system_calculated_score
                      belongs_to_current_user
                      is_viewable_by_current_user

                      bid {
                        bid_id
                        version
                        bid_amount
                        type
                        rank
                        record_status
                        terms {
                          status
                          offeror_type
                          buyers_premium_amount
                          payment_type
                          down_payment_amount
                          earnest_money_deposit_amount
                        }

                        negotiation_sequence {
                          latest_counter_bid {
                            bid_amount
                            bid_id
                            version
                            terms {
                              expiration_date
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          ${ONLINE_AUCTION_SEGMENT_BID_TERMS}
        `,
        variables: { listingId },
      },
      { req }
    )
    .then(res => res.body.data.listing)
// TODO for Mark move this to blueprint

export const ONLINE_AUCTION_SEGMENT_BID_TERMS = `
  fragment OnlineAuctionSegmentBidTermsFragment on OnlineAuctionSegmentBid {
    terms {
      seller_net_offer_amount
      buyer_net_offer_amount
      market_place_fee_amount
      market_place_fee_percentage
      status
      offer_amount
      offeror_type
      buyers_premium_amount
      seller_concession_amount
      earnest_money_deposit_amount
      down_payment_amount
      payment_type
      close_escrow_duration
      leaseback_amount
      leaseback_duration
      option_period_duration
      option_period_cost_amount
      inspection_contingency_period_duration
      is_agent_included
      financing_contingency_duration
      appraisal_contingency_duration
      seller_concession_amount
      notes {
        additional_notes
        latest_update_reason
      }
      expiration_date
      commission {
        paid_by
        agent_commission_amount
      }
      documents {
        document_id
        document_purpose_type
        urn
        document {
          file_descriptor {
            file
            content_type
          }
        }
      }
    }
  }
`

export const getBidHistoryForListing = (req, listingId) =>
  fetch
    .forAuctionGraph(
      {
        // eslint-disable-next-line @adc/named-gql-operations
        query: gql`
          query resiPdpBidHistoryOnListing($listingId: ID!) {
            listing(listingId: $listingId) {
              listing_id
              online_auction_segment {
                bids(filters: { take: 5 }) {
                  content {
                    bid_amount
                    belongs_to_current_user
                    type
                    bid_id
                  }
                }
              }
            }
          }
        `,
        variables: { listingId },
      },
      { req }
    )
    .then(
      res =>
        res.body?.data?.listing?.online_auction_segment?.bids?.content || []
    )
